import React, { useState } from "react";
import { MapPin, ExternalLink, Hotel, Clock, Utensils, Car, IdCard, Gift, Copy, Check } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";

const VENUES = [
  {
    id: "mirador",
    icon: MapPin,
    name: "Mirador de les Caves",
    subtitle: "Lugar de la ceremonia y celebración",
    address:
      "Ctra. d'Ordal a Sant Sadurní Km.3.800, Els Casots, 08739 Subirats, Barcelona",
    mapsUrl:
      "https://www.google.com/maps/search/?api=1&query=" +
      encodeURIComponent(
        "Mirador de les Caves, Ctra. d'Ordal a Sant Sadurní Km.3.800, Els Casots, 08739 Subirats, Barcelona"
      ),
  },
  {
    id: "hotel-domo",
    icon: Hotel,
    name: "Hotel Domo · Vilafranca del Penedès",
    subtitle: "Punto de recogida del bus y alojamiento recomendado",
    address: "Hotel Domo, Vilafranca del Penedès, Barcelona",
    mapsUrl: "https://www.google.com/maps/place/Hotel+Domo/@41.3415873,1.6914089,17z",
  },
];

const IBAN = "ES06 2100 3462 9622 0044 8085";

export default function Info() {
  const { t } = useI18n();
  return (
    <main data-testid="info-page" className="section">
      <h1 className="page-title font-script">{t.info.title}</h1>
      <p className="text-center text-[color:var(--ink-soft)] max-w-xl mx-auto mb-10">
        {t.info.subtitle}
      </p>

      {/* Venues */}
      <section className="max-w-2xl mx-auto space-y-4 mb-12">
        <p className="text-center text-[0.7rem] tracking-[0.32em] font-bold uppercase text-[color:var(--ink-wine)]/70">
          {t.info.howToGetThere}
        </p>
        {VENUES.map((v) => (
          <VenueCard key={v.id} venue={v} t={t} />
        ))}
      </section>

      {/* Hotel info */}
      <HotelInfo t={t} />

      {/* Gift */}
      <GiftCard t={t} />
    </main>
  );
}

function VenueCard({ venue, t }) {
  const Icon = venue.icon;
  const subtitle = t.info.venueSubtitles?.[venue.id] || venue.subtitle;
  return (
    <a
      href={venue.mapsUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`venue-${venue.id}`}
      className="block s-card s-card-pad group transition-all hover:shadow-md hover:-translate-y-0.5"
    >
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-10 h-10 rounded-full bg-[color:var(--bg-blush)] border border-[color:var(--line)] flex items-center justify-center text-[color:var(--ink-wine)]">
          <Icon size={18} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-script-alt text-2xl text-[color:var(--ink-wine)] leading-tight">
            {venue.name}
          </p>
          <p className="text-xs italic text-[color:var(--ink-soft)] mb-2">{subtitle}</p>
          <p className="text-sm text-[color:var(--ink-deep)] mb-3">{venue.address}</p>
          <span className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] font-bold uppercase text-[color:var(--olive-deep)] group-hover:text-[color:var(--ink-wine)] transition-colors">
            {t.info.openMaps}
            <ExternalLink size={12} />
          </span>
        </div>
      </div>
    </a>
  );
}

function HotelInfo({ t }) {
  const rows = [
    { icon: Clock, label: t.info.hotel.checkIn, value: t.info.hotel.checkInValue },
    { icon: Clock, label: t.info.hotel.checkOut, value: t.info.hotel.checkOutValue },
    { icon: Utensils, label: t.info.hotel.breakfast, value: t.info.hotel.breakfastValue },
    { icon: Car, label: t.info.hotel.parking, value: t.info.hotel.parkingValue },
  ];
  return (
    <section className="max-w-2xl mx-auto mb-12" data-testid="hotel-info">
      <p className="text-center text-[0.7rem] tracking-[0.32em] font-bold uppercase text-[color:var(--ink-wine)]/70 mb-4">
        {t.info.hotel.title}
      </p>
      <div className="s-card s-card-pad">
        <div className="flex items-start gap-3 mb-4">
          <span className="shrink-0 w-10 h-10 rounded-full bg-[color:var(--bg-blush)] border border-[color:var(--line)] flex items-center justify-center text-[color:var(--ink-wine)]">
            <Hotel size={18} />
          </span>
          <div>
            <p className="font-script-alt text-2xl text-[color:var(--ink-wine)] leading-tight">Hotel Domo</p>
            <p className="text-xs italic text-[color:var(--ink-soft)]">Vilafranca del Penedès</p>
          </div>
        </div>

        <ul className="space-y-2.5 mb-5">
          {rows.map(({ icon: Icon, label, value }, i) => (
            <li key={i} className="flex items-baseline gap-2.5 text-sm">
              <Icon size={14} className="shrink-0 text-[color:var(--olive-deep)] translate-y-0.5" />
              <span className="font-semibold text-[color:var(--ink-deep)]">{label}:</span>
              <span className="text-[color:var(--ink-deep)]">{value}</span>
            </li>
          ))}
        </ul>

        <div className="flex items-start gap-2.5 rounded-2xl bg-[color:var(--bg-blush)] px-4 py-3 border border-[color:var(--line)]">
          <IdCard size={16} className="shrink-0 text-[color:var(--ink-wine)] translate-y-0.5" />
          <p className="text-sm italic text-[color:var(--ink-deep)]">{t.info.hotel.tip}</p>
        </div>
      </div>
    </section>
  );
}

function GiftCard({ t }) {
  const [copied, setCopied] = useState(false);

  const copyIban = async () => {
    const plain = IBAN.replace(/\s+/g, "");
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(plain);
      } else {
        const el = document.createElement("textarea");
        el.value = plain;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      setCopied(true);
      toast.success(t.info.gift.copied);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("No se ha podido copiar. Selecciona y copia manualmente.");
    }
  };

  return (
    <section className="max-w-2xl mx-auto" data-testid="gift-section">
      <p className="text-center text-[0.7rem] tracking-[0.32em] font-bold uppercase text-[color:var(--ink-wine)]/70 mb-4">
        {t.info.gift.eyebrow}
      </p>
      <div className="s-card s-card-pad text-center">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[color:var(--bg-blush)] border border-[color:var(--line)] text-[color:var(--ink-wine)] mb-3">
          <Gift size={18} />
        </span>
        <h3 className="font-script text-3xl text-[color:var(--ink-wine)] leading-none mb-2">
          {t.info.gift.title}
        </h3>
        <p className="text-sm text-[color:var(--ink-deep)] italic max-w-md mx-auto mb-5">
          {t.info.gift.desc}
        </p>

        <button
          type="button"
          onClick={copyIban}
          data-testid="iban-copy"
          className="w-full max-w-md mx-auto flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-[color:var(--bg-blush)] border border-[color:var(--line)] hover:border-[color:var(--ink-wine)] transition-colors"
          aria-label={t.info.gift.copyAria}
        >
          <span className="font-mono text-sm sm:text-base tracking-wider text-[color:var(--ink-deep)] truncate">
            {IBAN}
          </span>
          <span className="shrink-0 inline-flex items-center gap-1.5 text-[0.65rem] tracking-[0.22em] font-bold uppercase text-[color:var(--olive-deep)]">
            {copied ? <><Check size={12} /> {t.info.gift.copiedShort}</> : <><Copy size={12} /> {t.info.gift.copy}</>}
          </span>
        </button>

        <p className="text-xs italic text-[color:var(--ink-soft)] mt-3">
          {t.info.gift.note}
        </p>
      </div>
    </section>
  );
}
